import React, { useEffect, useMemo, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router';
import { alpha, useTheme } from '@mui/material/styles';
import {
  AppBar, Toolbar, IconButton, Typography, Drawer, List, ListItem, ListItemButton, ListItemIcon,
  ListItemText, Avatar, Box, Menu, MenuItem, Divider, useMediaQuery, Tooltip,
  Snackbar, Alert, Paper, Chip, BottomNavigation, BottomNavigationAction,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import LogoutIcon from '@mui/icons-material/Logout';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { PORTAL_APP_BAR } from '../brand';
import { useAppDispatch, useAppSelector } from '../store/store';
import { clearSession } from '../store/authSlice';
import { hideMessage, showMessage, toggleColorMode } from '../store/uiSlice';
import { logout } from '../api/services';
import {
  getAppBottomBar, getNavItemDisplayLabel, isNavPathActive, visibleNavItems, type NavItem,
} from '../config/navConfig';
import { formatUserRoleForDisplay, isPayOnlyUser } from '../config/roleLabels';
import AppTourJoyride from './AppTourJoyride';

const DRAWER_WIDTH = 240;
const isDemo = process.env.REACT_APP_MODE?.toLowerCase() === 'demo';

function navItemIconColor(path: string): string {
  const map: Record<string, string> = {
    '/home': 'primary.main',
    '/admin/camaras': 'success.main',
    '/admin/fees': 'warning.main',
    '/admin/payments': 'info.main',
    '/admin/users': 'secondary.main',
    '/admin/logs': 'text.secondary',
  };
  return map[path] || 'action.active';
}

function entryKey(it: NavItem): string {
  return it.path;
}

export default function Layout() {
  const theme = useTheme();
  const colorMode = useAppSelector((s) => s.ui.colorMode);
  const isLgUp = useMediaQuery(theme.breakpoints.up('lg'));
  const showBottomBar = useMediaQuery(theme.breakpoints.down('lg'));
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [tourRun, setTourRun] = useState(false);
  const [profileEl, setProfileEl] = useState<HTMLElement | null>(null);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const user = useAppSelector((s) => s.auth.user);
  const snackbar = useAppSelector((s) => s.ui.snackbar);

  useEffect(() => {
    setDrawerOpen(isLgUp);
  }, [isLgUp]);

  useEffect(() => {
    if (tourRun && isLgUp) setDrawerOpen(true);
  }, [tourRun, isLgUp]);

  const items = useMemo(
    () => visibleNavItems(user?.roleName ?? null, !!user?.isSuperAdmin),
    [user?.roleName, user?.isSuperAdmin],
  );
  const bottomBar = useMemo(() => getAppBottomBar(items), [items]);
  const showMoreInBottomBar = bottomBar.length > 1;
  const moreTabValue = showMoreInBottomBar ? bottomBar.length : -1;
  const bottomValue = useMemo(() => {
    const i = bottomBar.findIndex((e) => isNavPathActive(e.path, location.pathname));
    if (i === -1) return false;
    return i;
  }, [bottomBar, location.pathname]);
  const navItemLabel = (it: NavItem) =>
    getNavItemDisplayLabel(it, user?.roleName ?? null, !!user?.isSuperAdmin);

  const payOnlyMode = isPayOnlyUser(user?.roleName ?? null, !!user?.isSuperAdmin);

  async function handleLogout() {
    await logout();
    dispatch(clearSession());
    navigate('/login', { replace: true });
  }

  const drawer = (
    <Box sx={{ width: DRAWER_WIDTH, height: '100%', display: 'flex', flexDirection: 'column', bgcolor: 'background.paper' }}>
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Avatar
          sx={{
            width: 36,
            height: 36,
            borderRadius: '50%',
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            fontSize: 14,
            fontWeight: 600,
            boxShadow: (t) => `0 2px 6px ${alpha(t.palette.primary.main, 0.35)}`,
          }}
        >
          C
        </Avatar>
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, lineHeight: 1.2 }}>{PORTAL_APP_BAR}</Typography>
        </Box>
      </Box>
      <Divider />
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <List data-tour="tour-drawer-list" sx={{ flex: 1, overflow: 'auto', py: 0.5 }}>
          {items.map((item) => {
            const selected = location.pathname === item.path
              || (item.path !== '/home' && location.pathname.startsWith(item.path));
            return (
              <ListItem key={item.path} disablePadding>
                <ListItemButton
                  component={NavLink}
                  to={item.path}
                  end={item.path === '/home'}
                  selected={selected}
                  onClick={() => { if (!isLgUp) setDrawerOpen(false); }}
                  sx={(t) => ({
                    mx: 0.5,
                    borderRadius: 0,
                    '&.Mui-selected, &.active': {
                      bgcolor: alpha(t.palette.primary.main, 0.1),
                      color: 'primary.main',
                      fontWeight: 600,
                      borderLeft: '3px solid',
                      borderColor: 'primary.main',
                      pl: 1.5,
                    },
                  })}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 40,
                      color: selected ? 'primary.main' : navItemIconColor(item.path),
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={navItemLabel(item)}
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
        <Divider />
        <List dense sx={{ py: 0.5 }}>
          <ListItem disablePadding>
            <ListItemButton
              onClick={() => {
                void handleLogout();
                if (!isLgUp) setDrawerOpen(false);
              }}
              sx={{
                mx: 0.5,
                borderRadius: 0,
                color: 'error.main',
                '&:hover': { bgcolor: (t) => alpha(t.palette.error.main, 0.08) },
              }}
            >
              <ListItemIcon sx={{ minWidth: 40, color: 'error.main' }}>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Cerrar sesión" primaryTypographyProps={{ variant: 'body2' }} />
            </ListItemButton>
          </ListItem>
        </List>
      </Box>
      <Divider />
      <Box sx={{ p: 2 }}>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            display: 'inline-block',
            px: 0.75,
            py: 0.25,
            borderRadius: 0,
            bgcolor: (t) => alpha(t.palette.primary.main, 0.08),
            color: 'primary.main',
            fontWeight: 600,
          }}
        >
          v1.0.0{process.env.REACT_APP_MODE ? ` · ${String(process.env.REACT_APP_MODE).toUpperCase()}` : ''}
        </Typography>
      </Box>
    </Box>
  );

  const mainPb = showBottomBar ? 9 : 3;
  const singleBottomNav = bottomBar.length === 1;
  /** Misma barra azul que en la referencia móvil (admin / operador comparten cromática). */
  const appBarBg = colorMode === 'dark' ? '#0d47a1' : theme.palette.primary.main;

  return (
    <Box
      sx={{
        display: 'flex',
        minHeight: '100vh',
        bgcolor: (t) => (showBottomBar && t.palette.mode === 'light' ? '#f0f2f5' : t.palette.background.default),
      }}
    >
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          zIndex: (t) => t.zIndex.drawer + 1,
          bgcolor: appBarBg,
          color: '#fff',
          backgroundImage: 'none',
          boxShadow: '0 2px 10px rgba(0,0,0,0.18)',
          borderBottom: 'none',
        }}
      >
        <Toolbar
          variant="dense"
          sx={{ minHeight: 52, gap: 0.5, px: { xs: 1, sm: 1.5 }, color: 'inherit' }}
        >
          <IconButton
            edge="start"
            data-tour="tour-menu-btn"
            onClick={() => setDrawerOpen((v) => !v)}
            aria-label="abrir menú"
            size="small"
            color="inherit"
            sx={{ mr: 0.5 }}
          >
            <MenuIcon fontSize="small" />
          </IconButton>
          <Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 1, overflow: 'hidden' }}>
            <Typography
              variant="subtitle1"
              component="span"
              noWrap
              sx={{ fontWeight: 800, color: 'inherit', lineHeight: 1.2 }}
            >
              {PORTAL_APP_BAR}
            </Typography>
            {isDemo ? (
              <Chip
                size="small"
                label="DEMO"
                sx={{
                  height: 24,
                  fontSize: 11,
                  fontWeight: 800,
                  flexShrink: 0,
                  bgcolor: theme.palette.warning.main,
                  color: theme.palette.warning.contrastText,
                  border: 'none',
                  borderRadius: '10px',
                }}
              />
            ) : (
              <Chip
                size="small"
                label="LIVE"
                sx={{
                  height: 24,
                  fontSize: 11,
                  fontWeight: 800,
                  flexShrink: 0,
                  color: '#fff',
                  bgcolor: alpha('#fff', 0.22),
                  border: '1px solid',
                  borderColor: alpha('#fff', 0.45),
                }}
              />
            )}
          </Box>

          <Box data-tour="tour-appbar-actions" sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flexShrink: 0 }}>
          <Tooltip title="Recorrido por la app">
            <IconButton
              size="small"
              color="inherit"
              aria-label="Recorrido guiado por la aplicación"
              onClick={() => setTourRun(true)}
            >
              <HelpOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={colorMode === 'dark' ? 'Modo claro' : 'Modo oscuro'}>
            <IconButton
              size="small"
              color="inherit"
              aria-label={colorMode === 'dark' ? 'Activar modo claro' : 'Activar modo oscuro'}
              onClick={() => dispatch(toggleColorMode())}
            >
              {colorMode === 'dark' ? <LightModeOutlinedIcon fontSize="small" /> : <DarkModeOutlinedIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
          {user && (
            <>
              <IconButton onClick={(e) => setProfileEl(e.currentTarget)} size="small" sx={{ p: 0.5, ml: 0.5 }} color="inherit">
                <Avatar
                  sx={{
                    width: 34,
                    height: 34,
                    borderRadius: '50%',
                    bgcolor: alpha('#fff', 0.22),
                    color: '#fff',
                    fontSize: 14,
                    fontWeight: 700,
                    border: 2,
                    borderColor: alpha('#fff', 0.85),
                    boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
                  }}
                >
                  {(user.firstName || user.dni || '?').charAt(0).toUpperCase()}
                </Avatar>
              </IconButton>
              <Menu anchorEl={profileEl} open={!!profileEl} onClose={() => setProfileEl(null)}>
                <Box sx={{ px: 2, py: 1, maxWidth: 280 }}>
                  <Typography variant="body2" fontWeight={600}>{user.firstName} {user.lastName}</Typography>
                  <Typography variant="caption" color="text.secondary" display="block">DNI {user.dni}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatUserRoleForDisplay(user.roleName, user.isSuperAdmin)}
                  </Typography>
                </Box>
                <Divider />
                <MenuItem onClick={handleLogout} dense sx={{ color: 'error.main' }}>
                  <ListItemIcon><LogoutIcon fontSize="small" color="error" /></ListItemIcon>
                  Cerrar sesión
                </MenuItem>
              </Menu>
            </>
          )}
          </Box>
        </Toolbar>
      </AppBar>

      <Drawer
        variant={isLgUp ? 'persistent' : 'temporary'}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        sx={{ width: DRAWER_WIDTH, flexShrink: 0, '& .MuiDrawer-paper': { width: DRAWER_WIDTH, boxSizing: 'border-box', borderRight: 1, borderColor: 'divider' } }}
      >
        <Toolbar />
        {drawer}
      </Drawer>

      <Box
        component="main"
        data-tour="tour-main-content"
        sx={{
          flexGrow: 1,
          p: { xs: 1.5, sm: 2, md: 3 },
          pb: { xs: mainPb, sm: 2, md: 3 },
          ml: isLgUp && drawerOpen ? `${DRAWER_WIDTH}px` : 0,
          transition: 'margin 0.2s',
          width: 1,
          minWidth: 0,
        }}
      >
        <Toolbar />
        <Outlet />
      </Box>

      {showBottomBar && (
        <Paper
          component="nav"
          data-tour="tour-bottom-nav"
          square={false}
          elevation={6}
          aria-label="Navegación principal"
          sx={{
            position: 'fixed',
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: (t) => t.zIndex.appBar,
            borderRadius: '16px 16px 0 0',
            borderTop: '1px solid',
            borderColor: 'divider',
            overflow: 'hidden',
            pb: 'max(0px, env(safe-area-inset-bottom, 0px))',
            bgcolor: 'background.paper',
            display: 'block',
          }}
        >
          <Box
            sx={{
              overflowX: 'auto',
              overflowY: 'hidden',
              WebkitOverflowScrolling: 'touch',
              maxWidth: '100%',
              '&::-webkit-scrollbar': { height: 3 },
            }}
          >
            <BottomNavigation
              showLabels
              value={bottomValue}
              onChange={(_, v) => {
                if (showMoreInBottomBar && v === moreTabValue) {
                  setDrawerOpen(true);
                  return;
                }
                const it = bottomBar[v as number];
                if (!it) return;
                if (isNavPathActive(it.path, location.pathname)) return;
                navigate(it.path);
              }}
              sx={{
                minHeight: 64,
                width: singleBottomNav ? '100%' : 'max-content',
                minWidth: '100%',
                alignItems: 'center',
                justifyContent: singleBottomNav ? 'center' : { xs: 'flex-start', sm: 'center' },
                bgcolor: 'background.paper',
                flexWrap: 'nowrap',
                '& .MuiBottomNavigationAction-root': singleBottomNav
                  ? { minWidth: 140, maxWidth: 280, flex: 1, py: 0.5, px: 1 }
                  : { minWidth: 64, maxWidth: 90, py: 0.5, px: 0.5 },
                '& .MuiBottomNavigationAction-label': {
                  fontSize: { xs: '0.6rem', sm: '0.65rem' },
                  fontWeight: 600,
                  lineHeight: 1.1,
                  opacity: 0.9,
                  '&.Mui-selected': { fontSize: { xs: '0.6rem', sm: '0.65rem' }, fontWeight: 800 },
                },
              }}
            >
              {bottomBar.map((it, i) => (
                <BottomNavigationAction
                  key={entryKey(it)}
                  label={navItemLabel(it)}
                  value={i}
                  icon={React.isValidElement(it.icon) ? React.cloneElement(it.icon, { fontSize: 'small' } as { fontSize?: string }) : it.icon}
                />
              ))}
              {showMoreInBottomBar && (
                <BottomNavigationAction
                  key="cajon-mas"
                  label="Más"
                  value={moreTabValue}
                  icon={<MoreHorizIcon fontSize="small" />}
                  sx={{ minWidth: 64 }}
                />
              )}
            </BottomNavigation>
          </Box>
        </Paper>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4500}
        onClose={() => dispatch(hideMessage())}
        anchorOrigin={{ vertical: 'bottom', horizontal: isLgUp ? 'right' : 'center' }}
        sx={showBottomBar ? { left: 8, right: 8, bottom: { xs: 80, sm: 80, md: 80 } } : { left: 8, right: 8, bottom: 8 }}
      >
        <Alert severity={snackbar.severity} variant="outlined" onClose={() => dispatch(hideMessage())} sx={{ width: '100%', maxWidth: { xs: '100%', sm: 400 } }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      <AppTourJoyride
        run={tourRun}
        onClose={() => setTourRun(false)}
        isLgUp={isLgUp}
        showBottomBar={showBottomBar}
        pathname={location.pathname}
        payOnlyMode={payOnlyMode}
        showBottomBarMore={showMoreInBottomBar}
      />
    </Box>
  );
}
